// ═══════════════════════════════════════════════════════════════
// Battle Rhythm — Templates
// Manage battle rhythm templates with event lists
// ═══════════════════════════════════════════════════════════════

import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Title, Text, Paper, Group, Stack, Button, TextInput, Textarea, Badge,
  Modal, ActionIcon, Select, SimpleGrid, Checkbox, NumberInput, Loader,
  Divider,
} from '../../components/gab/GabComponents';
import {
  IconPlus, IconTrash, IconTemplate, IconPlayerPlay, IconEdit,
  IconDeviceFloppy, IconDownload,
} from '@tabler/icons-react';
import { useBattleRhythmStore, type TemplateEvent, type BRTemplate } from '../../store/battlerhythmStore';
import { useAuthStore } from '../../store/authStore';

// ── Constants ────────────────────────────────────────────────

const EVENT_CATEGORIES: Record<string, { label: string; color: string }> = {
  briefing: { label: 'Briefing', color: '#4a82c4' },
  report: { label: 'Report Due', color: '#d97706' },
  shift: { label: 'Shift Change', color: '#8b5cf6' },
  external: { label: 'External', color: '#059669' },
  logistics: { label: 'Logistics', color: '#ea580c' },
  planning: { label: 'Planning', color: '#0891b2' },
  other: { label: 'Other', color: '#627d98' },
};

const CATEGORY_OPTIONS = Object.entries(EVENT_CATEGORIES).map(([value, { label }]) => ({ value, label }));
const OP_PERIOD_OPTIONS = [{ value: '12', label: '12 Hours' }, { value: '24', label: '24 Hours' }];

const DEFAULT_TEMPLATES: Record<string, { name: string; description: string; opHours: string; events: TemplateEvent[] }> = {
  '12h': {
    name: 'Standard 12h EOC',
    description: 'Standard 12-hour operational period template',
    opHours: '12',
    events: [
      { title: 'Shift Change', category: 'shift', scheduledTime: '06:00', durationMinutes: 30, location: 'EOC', responsible: 'IC', notes: '', recurring: true },
      { title: 'Situation Briefing', category: 'briefing', scheduledTime: '06:30', durationMinutes: 30, location: 'EOC', responsible: 'Planning', notes: '', recurring: true },
      { title: 'SitRep Due', category: 'report', scheduledTime: '09:00', durationMinutes: 15, location: '', responsible: 'Planning', notes: '', recurring: true },
      { title: 'Planning Meeting', category: 'planning', scheduledTime: '12:00', durationMinutes: 60, location: 'EOC', responsible: 'Planning Chief', notes: '', recurring: true },
      { title: 'Command Briefing', category: 'briefing', scheduledTime: '15:00', durationMinutes: 30, location: 'EOC', responsible: 'IC', notes: '', recurring: true },
      { title: 'Shift Change', category: 'shift', scheduledTime: '18:00', durationMinutes: 30, location: 'EOC', responsible: 'IC', notes: '', recurring: true },
    ],
  },
  '24h': {
    name: 'Standard 24h EOC',
    description: 'Full 24-hour operational period with shift changes, briefings, logistics, and reporting cycles',
    opHours: '24',
    events: [
      { title: 'Night Shift Handoff', category: 'briefing', scheduledTime: '06:00', durationMinutes: 30, location: 'EOC Main Floor', responsible: 'Outgoing IC', notes: 'Transfer command authority, review overnight activity', recurring: true },
      { title: 'Day Shift Briefing', category: 'briefing', scheduledTime: '06:30', durationMinutes: 30, location: 'EOC Briefing Room', responsible: 'IC', notes: 'Objectives, weather, safety message, section assignments', recurring: true },
      { title: 'Logistics Status Check', category: 'logistics', scheduledTime: '07:30', durationMinutes: 15, location: 'Logistics Section', responsible: 'Logistics Chief', notes: 'Resource orders, supply chain, facility status', recurring: true },
      { title: 'Morning SitRep', category: 'report', scheduledTime: '08:00', durationMinutes: 20, location: 'EOC', responsible: 'Planning Section', notes: 'Situation summary, damage assessment update, weather brief', recurring: true },
      { title: 'Public Information Update', category: 'communication', scheduledTime: '09:00', durationMinutes: 15, location: 'JIC', responsible: 'PIO', notes: 'Media update, social media posts, rumor control', recurring: true },
      { title: 'Operations Coordination Call', category: 'meeting', scheduledTime: '10:00', durationMinutes: 30, location: 'EOC Briefing Room', responsible: 'Ops Chief', notes: 'Field team status, resource deployment, mutual aid', recurring: true },
      { title: 'Shelter Status Report', category: 'report', scheduledTime: '11:00', durationMinutes: 15, location: 'ESF-6 Desk', responsible: 'Shelter Branch', notes: 'Census, capacity, supply needs', recurring: true },
      { title: 'Noon SitRep & Weather Update', category: 'report', scheduledTime: '12:00', durationMinutes: 20, location: 'EOC', responsible: 'Planning Section', notes: 'Midday situation update, NWS forecast', recurring: true },
      { title: 'Debris Ops Status', category: 'report', scheduledTime: '13:00', durationMinutes: 15, location: 'ESF-3 Desk', responsible: 'Debris Branch', notes: 'Tonnage hauled, crew deployment, disposal site status', recurring: true },
      { title: 'Finance/Admin Briefing', category: 'meeting', scheduledTime: '14:00', durationMinutes: 20, location: 'Finance Section', responsible: 'Finance Chief', notes: 'Cost tracking, procurement, FEMA documentation', recurring: true },
      { title: 'Afternoon Command Briefing', category: 'briefing', scheduledTime: '15:00', durationMinutes: 30, location: 'EOC Briefing Room', responsible: 'IC', notes: 'Full section chiefs brief, objective review', recurring: true },
      { title: 'Press Conference', category: 'communication', scheduledTime: '16:00', durationMinutes: 30, location: 'JIC / Virtual', responsible: 'PIO', notes: 'Public-facing update, Q&A with media', recurring: true },
      { title: 'Shift Change Prep', category: 'logistics', scheduledTime: '17:00', durationMinutes: 30, location: 'All Sections', responsible: 'Planning Section', notes: 'Prepare handoff notes, update ICS 214s', recurring: true },
      { title: 'Day/Night Shift Handoff', category: 'briefing', scheduledTime: '18:00', durationMinutes: 30, location: 'EOC Main Floor', responsible: 'IC', notes: 'Transfer command to night IC', recurring: true },
      { title: 'Night Shift Briefing', category: 'briefing', scheduledTime: '18:30', durationMinutes: 20, location: 'EOC Briefing Room', responsible: 'Night IC', notes: 'Night objectives, reduced staffing, escalation criteria', recurring: true },
      { title: 'Evening SitRep', category: 'report', scheduledTime: '20:00', durationMinutes: 15, location: 'EOC', responsible: 'Planning Section', notes: 'End-of-day summary, overnight watch priorities', recurring: true },
      { title: 'Overnight Status Check', category: 'report', scheduledTime: '00:00', durationMinutes: 10, location: 'EOC', responsible: 'Night Watch', notes: 'Facility status, weather monitoring', recurring: true },
      { title: 'Pre-Dawn Status Check', category: 'report', scheduledTime: '04:00', durationMinutes: 10, location: 'EOC', responsible: 'Night Watch', notes: 'Weather update, field team readiness', recurring: true },
    ],
  },
};

// ── Blank event helper ───────────────────────────────────────

function blankEvent(): TemplateEvent {
  return { title: '', category: 'other', scheduledTime: '08:00', durationMinutes: 30, location: '', responsible: '', notes: '', recurring: false };
}

// ── Component ────────────────────────────────────────────────

export default function BRTemplates() {
  const navigate = useNavigate();
  const { user, activeIncident } = useAuthStore();
  const {
    templates, activations, events: brEvents, loading,
    loadTemplates, loadActivations, loadEvents,
    createTemplate, updateTemplate, deleteTemplate,
  } = useBattleRhythmStore();

  // ── Modal state ────────────────────────────────────────────
  const [createOpen, setCreateOpen] = useState(false);
  const [editTemplate, setEditTemplate] = useState<BRTemplate | null>(null);
  const [saveFromOpen, setSaveFromOpen] = useState(false);

  // ── Form fields ────────────────────────────────────────────
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [opHours, setOpHours] = useState<string>('12');
  const [eventList, setEventList] = useState<TemplateEvent[]>([blankEvent()]);

  // ── Save-from-activation fields ────────────────────────────
  const [saveActivationId, setSaveActivationId] = useState<string | null>(null);
  const [saveName, setSaveName] = useState('');
  const [saveDescription, setSaveDescription] = useState('');

  // ── Load data ──────────────────────────────────────────────
  useEffect(() => {
    loadTemplates();
    if (activeIncident) {
      loadActivations(String(activeIncident.id));
      loadEvents(String(activeIncident.id));
    }
  }, [activeIncident]);

  // ── Helpers ────────────────────────────────────────────────

  const parseEvents = (json: string): TemplateEvent[] => {
    try { return JSON.parse(json) as TemplateEvent[]; } catch { return []; }
  };

  const resetForm = useCallback(() => {
    setName('');
    setDescription('');
    setOpHours('12');
    setEventList([blankEvent()]);
    setEditTemplate(null);
  }, []);

  const openCreate = () => {
    resetForm();
    setCreateOpen(true);
  };

  const openEdit = (t: BRTemplate) => {
    setEditTemplate(t);
    setName(t.name);
    setDescription(t.description);
    setOpHours(t.opHours || '12');
    setEventList(parseEvents(t.events));
    setCreateOpen(true);
  };

  // ── Event list manipulation ────────────────────────────────

  const addEvent = () => setEventList([...eventList, blankEvent()]);

  const removeEvent = (i: number) => setEventList(eventList.filter((_, idx) => idx !== i));

  const updateEventField = (i: number, field: keyof TemplateEvent, value: string | number | boolean) => {
    const updated = [...eventList];
    updated[i] = { ...updated[i], [field]: value };
    setEventList(updated);
  };

  // ── Save / Update ─────────────────────────────────────────

  const handleSave = async () => {
    if (!name.trim()) return;
    const validEvents = eventList.filter(e => e.title.trim());
    const payload = {
      name: name.trim(),
      description: description.trim(),
      opHours,
      events: JSON.stringify(validEvents),
      creator: user?.fullName || '',
    };
    if (editTemplate) {
      await updateTemplate(editTemplate.id, payload);
    } else {
      await createTemplate(payload);
    }
    setCreateOpen(false);
    resetForm();
  };

  // ── Save from activation ───────────────────────────────────

  const handleSaveFromActivation = async () => {
    if (!saveName.trim() || !saveActivationId) return;
    const incidentEvents = brEvents.filter(e =>
      String(e.incidentId) === String(activeIncident?.id)
    );
    const templateEvents: TemplateEvent[] = incidentEvents.map(e => ({
      title: e.title,
      category: e.category,
      scheduledTime: e.scheduledTime,
      durationMinutes: Number(e.durationMinutes) || 30,
      location: e.location,
      responsible: e.responsible,
      notes: e.notes,
      recurring: e.recurring === 'true',
    }));
    const activation = activations.find(a => String(a.id) === saveActivationId);
    await createTemplate({
      name: saveName.trim(),
      description: saveDescription.trim(),
      opHours: activation?.opHours || '12',
      events: JSON.stringify(templateEvents),
      creator: user?.fullName || '',
    });
    setSaveFromOpen(false);
    setSaveName('');
    setSaveDescription('');
    setSaveActivationId(null);
  };

  // ── Use template (navigate to dashboard with template state) ─

  const handleUseTemplate = (t: BRTemplate) => {
    navigate('/battlerhythm', { state: { useTemplate: t } });
  };

  const handleUseDefault = async (key: string) => {
    const def = DEFAULT_TEMPLATES[key];
    // Create a template record first, then navigate
    const rid = await createTemplate({
      name: def.name,
      description: def.description,
      opHours: def.opHours,
      events: JSON.stringify(def.events),
      creator: user?.fullName || '',
    });
    // Navigate with that template
    const saved = useBattleRhythmStore.getState().templates.find(t => t.id === rid);
    if (saved) {
      navigate('/battlerhythm', { state: { useTemplate: saved } });
    }
  };

  // ── Delete ─────────────────────────────────────────────────

  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this template?')) return;
    await deleteTemplate(id);
  };

  // ── Determine which defaults are already saved ─────────────

  const defaultNames = new Set(templates.map(t => t.name));
  const availableDefaults = Object.entries(DEFAULT_TEMPLATES).filter(
    ([, def]) => !defaultNames.has(def.name)
  );

  // ── Render ─────────────────────────────────────────────────

  if (loading) {
    return (
      <Group justify="center" p="xl">
        <Loader size="sm" />
        <Text size="sm" c="dimmed">Loading templates...</Text>
      </Group>
    );
  }

  return (
    <div>
      {/* Header */}
      <Group justify="space-between" mb="lg">
        <Title order={3} c="black">Templates</Title>
        <Group gap="xs">
          {activeIncident && activations.length > 0 && (
            <Button
              variant="light"
              size="xs"
              leftSection={<IconDeviceFloppy size={14} />}
              onClick={() => setSaveFromOpen(true)}
            >
              Save Current as Template
            </Button>
          )}
          <Button
            size="xs"
            leftSection={<IconPlus size={14} />}
            onClick={openCreate}
          >
            Create Template
          </Button>
        </Group>
      </Group>

      {/* Pre-Built Templates */}
      {availableDefaults.length > 0 && (
        <>
          <Text fw={600} size="sm" mb="xs" c="dimmed">Pre-Built Templates</Text>
          <SimpleGrid cols={{ base: 1, sm: 2 }} mb="lg" spacing="sm">
            {availableDefaults.map(([key, def]) => (
              <Paper key={key} p="md" radius="sm" withBorder style={{ borderStyle: 'dashed' }}>
                <Group justify="space-between" mb="xs">
                  <Group gap="xs">
                    <IconTemplate size={16} color="#8b5cf6" />
                    <Text fw={600} size="sm">{def.name}</Text>
                  </Group>
                  <Badge size="xs" variant="light" color="violet">{def.opHours}h</Badge>
                </Group>
                <Text size="xs" c="dimmed" mb="sm">{def.description}</Text>
                <Group gap={4} mb="sm" wrap="wrap">
                  {def.events.map((ev, i) => {
                    const cat = EVENT_CATEGORIES[ev.category] || EVENT_CATEGORIES.other;
                    return (
                      <Badge key={i} size="xs" variant="dot" style={{ borderColor: cat.color, color: cat.color }}>
                        {ev.title}
                      </Badge>
                    );
                  })}
                </Group>
                <Button
                  size="xs"
                  variant="light"
                  color="violet"
                  leftSection={<IconDownload size={14} />}
                  onClick={() => handleUseDefault(key)}
                  fullWidth
                >
                  Use This Template
                </Button>
              </Paper>
            ))}
          </SimpleGrid>
        </>
      )}

      {/* My Templates */}
      <Text fw={600} size="sm" mb="xs" c="dimmed">My Templates</Text>
      {templates.length === 0 ? (
        <Paper p="xl" radius="sm" withBorder>
          <Text ta="center" c="dimmed" size="sm">No templates yet. Create one or use a pre-built template above.</Text>
        </Paper>
      ) : (
        <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="sm">
          {templates.map(t => {
            const events = parseEvents(t.events);
            return (
              <Paper key={t.id} p="md" radius="sm" withBorder>
                <Group justify="space-between" mb="xs">
                  <Text fw={600} size="sm" lineClamp={1}>{t.name}</Text>
                  <Badge size="xs" variant="light" color="violet">{t.opHours || '12'}h</Badge>
                </Group>
                {t.description && (
                  <Text size="xs" c="dimmed" mb="xs" lineClamp={2}>{t.description}</Text>
                )}
                <Group gap="xs" mb="xs">
                  <Badge size="xs" variant="outline" color="gray">
                    {events.length} event{events.length !== 1 ? 's' : ''}
                  </Badge>
                </Group>
                <Group gap={4} mb="sm" wrap="wrap">
                  {events.slice(0, 8).map((ev, i) => {
                    const cat = EVENT_CATEGORIES[ev.category] || EVENT_CATEGORIES.other;
                    return (
                      <Badge key={i} size="xs" variant="dot" style={{ borderColor: cat.color, color: cat.color }}>
                        {ev.title}
                      </Badge>
                    );
                  })}
                  {events.length > 8 && (
                    <Badge size="xs" variant="outline" color="gray">+{events.length - 8}</Badge>
                  )}
                </Group>
                <Divider mb="xs" />
                <Group gap="xs">
                  <Button size="xs" variant="light" color="violet" leftSection={<IconPlayerPlay size={14} />}
                    onClick={() => handleUseTemplate(t)} style={{ flex: 1 }}>
                    Use
                  </Button>
                  <ActionIcon variant="subtle" color="gray" size="sm" onClick={() => openEdit(t)}>
                    <IconEdit size={14} />
                  </ActionIcon>
                  <ActionIcon variant="subtle" color="red" size="sm" onClick={() => handleDelete(t.id)}>
                    <IconTrash size={14} />
                  </ActionIcon>
                </Group>
              </Paper>
            );
          })}
        </SimpleGrid>
      )}

      {/* ── Create / Edit Template Modal ────────────────────── */}
      <Modal
        opened={createOpen}
        onClose={() => { setCreateOpen(false); resetForm(); }}
        title={editTemplate ? 'Edit Template' : 'Create Template'}
        size="xl"
      >
        <Stack gap="sm">
          <TextInput
            label="Template Name"
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="e.g., Tornado 12h EOC Activation"
            required
          />
          <Textarea
            label="Description"
            value={description}
            onChange={e => setDescription(e.target.value)}
            minRows={2}
            placeholder="Brief description of this template"
          />
          <Select
            label="Operational Period"
            data={OP_PERIOD_OPTIONS}
            value={opHours}
            onChange={v => setOpHours(v || '12')}
            allowDeselect={false}
          />

          <Divider />
          <Group justify="space-between">
            <Text fw={600} size="sm">Events ({eventList.length})</Text>
            <Button variant="subtle" size="xs" leftSection={<IconPlus size={12} />} onClick={addEvent}>
              Add Event
            </Button>
          </Group>

          {eventList.map((ev, i) => (
            <Paper key={i} p="sm" radius="sm" withBorder style={{ background: 'var(--mantine-color-gray-0)' }}>
              <Group justify="space-between" mb="xs">
                <Text size="xs" fw={600} c="dimmed">Event {i + 1}</Text>
                {eventList.length > 1 && (
                  <ActionIcon variant="subtle" color="red" size="xs" onClick={() => removeEvent(i)}>
                    <IconTrash size={12} />
                  </ActionIcon>
                )}
              </Group>
              <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="xs">
                <TextInput
                  label="Title"
                  size="xs"
                  value={ev.title}
                  onChange={e => updateEventField(i, 'title', e.target.value)}
                  required
                />
                <Select
                  label="Category"
                  size="xs"
                  data={CATEGORY_OPTIONS}
                  value={ev.category}
                  onChange={v => updateEventField(i, 'category', v || 'other')}
                  allowDeselect={false}
                />
                <TextInput
                  label="Scheduled Time"
                  size="xs"
                  type="time"
                  value={ev.scheduledTime}
                  onChange={e => updateEventField(i, 'scheduledTime', e.target.value)}
                />
              </SimpleGrid>
              <SimpleGrid cols={{ base: 1, sm: 4 }} spacing="xs" mt="xs">
                <NumberInput
                  label="Duration (min)"
                  size="xs"
                  value={ev.durationMinutes}
                  onChange={v => updateEventField(i, 'durationMinutes', Number(v) || 30)}
                  min={5}
                  max={480}
                />
                <TextInput
                  label="Location"
                  size="xs"
                  value={ev.location}
                  onChange={e => updateEventField(i, 'location', e.target.value)}
                />
                <TextInput
                  label="Responsible"
                  size="xs"
                  value={ev.responsible}
                  onChange={e => updateEventField(i, 'responsible', e.target.value)}
                />
                <Checkbox
                  label="Recurring"
                  size="xs"
                  checked={ev.recurring}
                  onChange={e => updateEventField(i, 'recurring', e.currentTarget.checked)}
                  mt={24}
                />
              </SimpleGrid>
              <Textarea
                label="Notes"
                size="xs"
                value={ev.notes}
                onChange={e => updateEventField(i, 'notes', e.target.value)}
                minRows={1}
                mt="xs"
              />
            </Paper>
          ))}

          <Group justify="flex-end" mt="sm">
            <Button variant="subtle" onClick={() => { setCreateOpen(false); resetForm(); }}>
              Cancel
            </Button>
            <Button onClick={handleSave} leftSection={<IconDeviceFloppy size={14} />}>
              {editTemplate ? 'Update Template' : 'Save Template'}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* ── Save from Activation Modal ──────────────────────── */}
      <Modal
        opened={saveFromOpen}
        onClose={() => { setSaveFromOpen(false); setSaveName(''); setSaveDescription(''); setSaveActivationId(null); }}
        title="Save Current Activation as Template"
        size="md"
      >
        <Stack gap="sm">
          <Select
            label="Select Activation"
            placeholder="Choose an activation"
            data={activations.map(a => ({
              value: String(a.id),
              label: `${a.opHours}h Op Period — ${a.status} (${a.datecreated ? new Date(a.datecreated).toLocaleDateString() : 'unknown date'})`,
            }))}
            value={saveActivationId}
            onChange={v => setSaveActivationId(v)}
          />
          <TextInput
            label="Template Name"
            value={saveName}
            onChange={e => setSaveName(e.target.value)}
            placeholder="e.g., Hurricane Alpha Pattern"
            required
          />
          <Textarea
            label="Description"
            value={saveDescription}
            onChange={e => setSaveDescription(e.target.value)}
            minRows={2}
            placeholder="What makes this rhythm pattern useful?"
          />
          <Group justify="flex-end" mt="sm">
            <Button variant="subtle" onClick={() => { setSaveFromOpen(false); setSaveName(''); setSaveDescription(''); setSaveActivationId(null); }}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveFromActivation}
              leftSection={<IconDeviceFloppy size={14} />}
              disabled={!saveName.trim() || !saveActivationId}
            >
              Save as Template
            </Button>
          </Group>
        </Stack>
      </Modal>
    </div>
  );
}
